import React, { useState } from 'react';
import {
  Modal,
  Stack,
  Text,
  Button,
  Group,
  Switch,
  Alert,
  Title,
  useMantineColorScheme,
} from '@mantine/core';
import {
  IconCheck,
  IconX,
  IconInfoCircle,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import CardTokenForm from '../payments/CardTokenForm';
import { 
  paymentMethodService,
  CreatePaymentMethodRequest
} from '../../services/paymentMethodService';

interface AddPaymentMethodModalProps {
  opened: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const AddPaymentMethodModal: React.FC<AddPaymentMethodModalProps> = ({
  opened,
  onClose,
  onSuccess
}) => {
  const { colorScheme } = useMantineColorScheme();
  const [loading, setLoading] = useState(false);
  const [setAsDefault, setSetAsDefault] = useState(false);

  const contentStyle = {
    backgroundColor: colorScheme === 'dark' ? 'rgba(30, 41, 59, 0.7)' : 'rgba(247, 243, 238, 0.95)',
    border: `1px solid ${colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(221, 216, 209, 0.8)'}`,
  } as const;

  const handleCardTokenReceived = async (cardData: any) => {
    if (!cardData || !cardData.token) {
      notifications.show({
        color: 'red',
        title: 'Error',
        message: 'No se pudo obtener el token de la tarjeta',
        icon: <IconX size={16} />,
      });
      return;
    }

    try {
      setLoading(true);
      
      const request: CreatePaymentMethodRequest = {
        cardToken: cardData.token,
        cardBrand: cardData.payment_method_id?.toUpperCase() || 'UNKNOWN',
        cardLastFour: cardData.last_four_digits || '0000',
        cardHolderName: cardData.cardholder?.name || '',
        expirationMonth: parseInt(cardData.expiration_month) || 12,
        expirationYear: parseInt(cardData.expiration_year) || new Date().getFullYear(),
        setAsDefault: setAsDefault
      };

      await paymentMethodService.createPaymentMethod(request);

      notifications.show({
        color: 'green',
        title: 'Método de pago agregado',
        message: 'Tu método de pago ha sido guardado exitosamente',
        icon: <IconCheck size={16} />,
      });

      onSuccess();
      onClose();

    } catch (error: any) {
      console.error('Error saving payment method:', error);
      notifications.show({
        color: 'red',
        title: 'Error',
        message: error?.response?.data?.message || 'No se pudo guardar el método de pago',
        icon: <IconX size={16} />,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleError = (error: any) => {
    console.error('MercadoPago form error:', error);
    notifications.show({
      color: 'red',
      title: 'Error en el formulario',
      message: 'Por favor verifica los datos de tu tarjeta',
      icon: <IconX size={16} />,
    });
    setLoading(false);
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title="Agregar método de pago"
      size="xl"
      radius="lg"
      centered
      closeOnClickOutside={!loading}
      closeOnEscape={!loading}
      styles={{
        content: { 
          ...contentStyle, 
          padding: '24px',
          maxHeight: '90vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        },
        header: { 
          backgroundColor: 'transparent',
          borderBottom: 'none',
          paddingBottom: '16px',
          position: 'static',
          marginBottom: 0,
          border: 'none'
        },
        body: {
          overflowY: 'auto',
          flex: 1
        },
        title: { 
          color: colorScheme === 'dark' ? '#e2e8f0' : '#2d2a26',
          width: '100%',
          fontSize: '1.5rem',
          fontWeight: 600
        },
      }}
    >
      <Stack gap="lg">
        <Alert 
          icon={<IconInfoCircle size={20} />}
          styles={{
            root: {
              backgroundColor: colorScheme === 'dark' ? 'rgba(190, 197, 164, 0.2)' : 'rgba(190, 197, 164, 0.15)',
              border: `1px solid ${colorScheme === 'dark' ? 'rgba(190, 197, 164, 0.4)' : 'rgba(190, 197, 164, 0.3)'}`,
            },
            message: {
              color: colorScheme === 'dark' ? '#e2e8f0' : '#2d2a26',
            },
            icon: {
              color: '#BEC5A4'
            }
          }}
        >
          Ingresa los datos de tu tarjeta para guardarla como método de pago. 
          Podrás usarla para renovaciones automáticas y futuros pagos.
        </Alert>

        {/* Formulario de MercadoPago reutilizado */}
        <CardTokenForm
          onTokenReceived={handleCardTokenReceived}
          onError={handleError}
          loading={loading}
        />

        {/* Opción para establecer como predeterminado */}
        <Group justify="space-between" align="center">
          <div>
            <Text size="sm" fw={500}>
              Establecer como método predeterminado
            </Text>
            <Text size="xs" c="dimmed">
              Este método se usará para renovaciones automáticas
            </Text>
          </div>
          <Switch
            checked={setAsDefault}
            onChange={(event) => setSetAsDefault(event.currentTarget.checked)}
            disabled={loading}
            styles={{
              track: {
                backgroundColor: setAsDefault ? '#BEC5A4' : undefined,
                borderColor: '#BEC5A4'
              }
            }}
          />
        </Group>

        <Group justify="flex-end" mt="md">
          <Button 
            variant="subtle" 
            onClick={handleClose}
            disabled={loading}
            color="gray"
          >
            Cancelar
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default AddPaymentMethodModal;